import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import {
  House,
  Clock,
  ChartBar,
  Calendar,
  Image,
  Users,
  CheckSquare,
  IdentificationBadge,
  SignOut,
  Globe,
  FileText,
  Receipt,
  LockKey,
  Archive,
  Key,
  CurrencyEur,
  HandCoins,
  List as MenuIcon,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useState } from "react";
import api, { formatError } from "../lib/api";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_a323fd97-8209-4ccf-b91c-22f7e7a3b086/artifacts/sfihrkca_WhatsApp%20Image%202026-04-22%20at%2022.40.09.jpeg";

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [pwdOpen, setPwdOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });

  const submitPwd = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) {
      toast.error(t("password.mismatch"));
      return;
    }
    try {
      await api.put("/auth/password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      toast.success(t("password.changed"));
      setPwdOpen(false);
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const adminLinks = [
    { to: "/", label: t("nav.dashboard"), icon: House, testid: "nav-dashboard" },
    { to: "/horas", label: t("nav.horas"), icon: Clock, testid: "nav-horas" },
    { to: "/aprobaciones", label: t("nav.aprobaciones"), icon: CheckSquare, testid: "nav-aprobaciones" },
    { to: "/agenda", label: t("nav.agenda"), icon: Calendar, testid: "nav-agenda" },
    { to: "/fotos", label: t("nav.fotos"), icon: Image, testid: "nav-fotos" },
    { to: "/reportes", label: t("nav.reportes"), icon: ChartBar, testid: "nav-reportes" },
    { to: "/cierres", label: t("nav.cierres"), icon: LockKey, testid: "nav-cierres" },
    { to: "/presupuestos", label: t("nav.presupuestos"), icon: FileText, testid: "nav-presupuestos" },
    { to: "/facturas", label: t("nav.facturas"), icon: Receipt, testid: "nav-facturas" },
    { to: "/archivo", label: t("nav.archivo"), icon: Archive, testid: "nav-archivo" },
    { to: "/ingresos", label: t("nav.ingresos"), icon: HandCoins, testid: "nav-ingresos" },
    { to: "/gastos", label: t("nav.gastos"), icon: CurrencyEur, testid: "nav-gastos" },
    { to: "/clientes", label: t("nav.clientes"), icon: Users, testid: "nav-clientes" },
    { to: "/funcionarios", label: t("nav.funcionarios"), icon: IdentificationBadge, testid: "nav-funcionarios" },
  ];

  const empLinks = [
    { to: "/", label: t("nav.dashboard"), icon: House, testid: "nav-dashboard" },
    { to: "/horas", label: t("nav.horas"), icon: Clock, testid: "nav-horas" },
    { to: "/agenda", label: t("nav.agenda"), icon: Calendar, testid: "nav-agenda" },
    { to: "/fotos", label: t("nav.fotos"), icon: Image, testid: "nav-fotos" },
    { to: "/reportes", label: t("nav.reportes"), icon: ChartBar, testid: "nav-reportes" },
  ];

  const links = isAdmin ? adminLinks : empLinks;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const changeLang = (lng) => {
    i18n.changeLanguage(lng);
  };

  const closeDrawer = () => setDrawerOpen(false);

  const SidebarContent = ({ onNavigate }) => (
    <div className="flex flex-col h-full bg-white">
      <div className="p-5 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Modolo" className="w-11 h-11 rounded-lg object-cover" />
          <div>
            <div className="heading-font font-bold text-slate-900 text-sm leading-tight">Modolo</div>
            <div className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">
              Multiservices
            </div>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {links.map((link) => {
          const Icon = link.icon;
          return (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={link.testid}
              onClick={onNavigate}
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
            >
              <Icon size={18} weight="duotone" />
              <span>{link.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="p-3 border-t border-slate-200 space-y-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="w-full justify-start gap-2 text-slate-600"
              data-testid="language-switcher"
            >
              <Globe size={18} weight="duotone" />
              <span className="text-sm">{t("lang.label")}: {i18n.language === "pt" ? "PT" : "ES"}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onClick={() => changeLang("es")} data-testid="lang-es">
              ES {t("lang.es")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => changeLang("pt")} data-testid="lang-pt">
              PT {t("lang.pt")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="flex items-center gap-3 px-2 py-2 rounded-lg bg-slate-50">
          <div className="w-9 h-9 rounded-full brand-gradient flex items-center justify-center text-white font-semibold text-sm">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900 truncate" data-testid="user-name">
              {user?.name}
            </div>
            <div className="text-[11px] text-slate-500 uppercase tracking-wider">
              {user?.role === "admin" ? t("funcionarios.admin") : t("funcionarios.employee")}
            </div>
          </div>
        </div>

        <Button
          variant="ghost"
          className="w-full justify-start gap-2 text-slate-600"
          onClick={() => { setPwdOpen(true); if (onNavigate) onNavigate(); }}
          data-testid="change-password-btn"
        >
          <Key size={18} weight="duotone" />
          <span className="text-sm">{t("password.change")}</span>
        </Button>

        <Button
          variant="ghost"
          className="w-full justify-start gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <SignOut size={18} weight="duotone" />
          <span className="text-sm">{t("nav.logout")}</span>
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="app-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 border-r border-slate-200 flex-col fixed h-screen z-30">
        <SidebarContent />
      </aside>

      {/* Mobile Topbar */}
      <header className="md:hidden fixed top-0 inset-x-0 h-14 bg-white border-b border-slate-200 z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" className="h-9 w-9 p-0" data-testid="mobile-menu-btn">
                <MenuIcon size={22} weight="bold" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72 max-w-[85vw]">
              <SheetTitle className="sr-only">Modolo Multiservices S.L.</SheetTitle>
              <SidebarContent onNavigate={closeDrawer} />
            </SheetContent>
          </Sheet>
          <img src={LOGO_URL} alt="Modolo" className="w-8 h-8 rounded-md object-cover" />
          <div className="heading-font font-bold text-slate-900 text-sm">Modolo</div>
        </div>
        <div className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">
          {user?.role === "admin" ? t("funcionarios.admin") : t("funcionarios.employee")}
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 md:ml-64 pt-14 md:pt-0 px-4 py-5 md:p-8 lg:p-10 fade-in-up min-w-0">{children}</main>

      {/* Change password dialog */}
      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent className="max-w-md" data-testid="password-dialog">
          <DialogHeader>
            <DialogTitle>{t("password.change")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitPwd} className="space-y-4">
            <div>
              <Label>{t("password.current")}</Label>
              <Input
                type="password"
                value={pwd.current_password}
                onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
                required
                data-testid="current-password-input"
              />
            </div>
            <div>
              <Label>{t("password.new")}</Label>
              <Input
                type="password"
                value={pwd.new_password}
                onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
                minLength={6}
                required
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label>{t("password.confirm")}</Label>
              <Input
                type="password"
                value={pwd.confirm}
                onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                minLength={6}
                required
                data-testid="confirm-password-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPwdOpen(false)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="submit-password-btn">
                {t("password.save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
